import { useRef, useCallback } from 'react';
import useGetRotationV2 from 'src/api/useGetRotationV2';
import useGetStowageRules from 'src/api/useGetStowageRules';
import { useRotationStore } from 'src/context/RotationContext';
import { useStowageRuleStore } from 'src/context/StowageRuleContext';

/**
 * Result type for preloaded base stowage data.
 * Uses 'any' type for data since the actual types are inferred from the API responses.
 */
export interface PreloadResult {
    success: boolean;
    data?: {
        portNames: any;
        berthNames: any;
        products: any;
        productCategories: any;
        coatings: any;
        uscgCompatibilities: any;
        stowageRuleMaps: any;
    };
    error?: unknown;
}

// Module-level singleton state to track the preload across all hook instances
// This ensures all components share the same preload state
interface PreloadState {
    promise: Promise<PreloadResult> | null;
    data: PreloadResult['data'] | null;
    hasStarted: boolean;
}

const preloadState: PreloadState = {
    promise: null,
    data: null,
    hasStarted: false,
};

/**
 * Hook for preloading base stowage data during app initialization.
 * This allows the stowage base data to be fetched in parallel with other
 * initialization calls (GetVessels, GetRoles, etc.) instead of waiting
 * for them to complete.
 *
 * Usage:
 * 1. Call startPreload() during app initialization (in RouterContent)
 * 2. Call getPreloadedData() when the data is needed (in getMasterStowagePlanFromApi)
 *
 * The preload state is shared across all hook instances via module-level state.
 */
const usePreloadBaseStowageData = () => {
    const { getPortNames, getBerthNames, getProducts, getProductCategories, getCoatings, getUscgCompatibilities } =
        useGetRotationV2();
    const { getStowageRulesMaps } = useGetStowageRules();

    const { portNameList, berthNameList, productList, productCategoryList, coatingList, uscgCompatibilities } =
        useRotationStore();

    const { stowageRulesMaps } = useStowageRuleStore();

    // Store fetcher functions in a ref so we can access them in the preload function
    // This is needed because the preload runs asynchronously and we need stable references
    const fetchersRef = useRef({
        getPortNames,
        getBerthNames,
        getProducts,
        getProductCategories,
        getCoatings,
        getUscgCompatibilities,
        getStowageRulesMaps,
    });

    // Update ref on each render to ensure we have the latest functions
    fetchersRef.current = {
        getPortNames,
        getBerthNames,
        getProducts,
        getProductCategories,
        getCoatings,
        getUscgCompatibilities,
        getStowageRulesMaps,
    };

    // Store cached data in a ref for the same reason
    const cachedDataRef = useRef({
        portNameList,
        berthNameList,
        productList,
        productCategoryList,
        coatingList,
        uscgCompatibilities,
        stowageRulesMaps,
    });

    cachedDataRef.current = {
        portNameList,
        berthNameList,
        productList,
        productCategoryList,
        coatingList,
        uscgCompatibilities,
        stowageRulesMaps,
    };

    /**
     * Start preloading base stowage data in the background.
     * This should be called during app initialization.
     * Safe to call multiple times - subsequent calls are no-ops.
     */
    const startPreload = useCallback(() => {
        // Don't start if already in progress or completed
        if (preloadState.hasStarted) {
            return;
        }
        preloadState.hasStarted = true;

        const fetchers = fetchersRef.current;
        const cached = cachedDataRef.current;

        // Helper function to use cached data if available
        const fetchOrCached = async <T>(cachedValue: T | null, fetcher: () => Promise<T | undefined>): Promise<T | undefined> => {
            if (cachedValue && (Array.isArray(cachedValue) ? cachedValue.length > 0 : true)) {
                return cachedValue;
            }
            return fetcher();
        };

        preloadState.promise = (async (): Promise<PreloadResult> => {
            try {
                const results = await Promise.all([
                    fetchOrCached(cached.portNameList, fetchers.getPortNames),
                    fetchOrCached(cached.berthNameList, fetchers.getBerthNames),
                    fetchOrCached(cached.productList, fetchers.getProducts),
                    fetchOrCached(cached.productCategoryList, fetchers.getProductCategories),
                    fetchOrCached(cached.coatingList, fetchers.getCoatings),
                    fetchOrCached(cached.uscgCompatibilities, fetchers.getUscgCompatibilities),
                    fetchOrCached(cached.stowageRulesMaps, fetchers.getStowageRulesMaps),
                ]);

                preloadState.data = {
                    portNames: results[0],
                    berthNames: results[1],
                    products: results[2],
                    productCategories: results[3],
                    coatings: results[4],
                    uscgCompatibilities: results[5],
                    stowageRuleMaps: results[6],
                };

                return { success: true, data: preloadState.data };
            } catch (error) {
                console.error('Error preloading base stowage data:', error);
                return { success: false, error };
            }
        })();
    }, []);

    /**
     * Get the preloaded data. If preloading hasn't completed yet,
     * this will wait for it. If preloading hasn't started, it will
     * start it and wait.
     */
    const getPreloadedData = useCallback(async (): Promise<PreloadResult> => {
        // If we have cached data, return immediately
        if (preloadState.data) {
            return { success: true, data: preloadState.data };
        }

        // If preload hasn't started, start it
        if (!preloadState.promise) {
            startPreload();
        }

        // Wait for the preload to complete
        if (preloadState.promise) {
            return preloadState.promise;
        }

        // Fallback - this shouldn't happen
        return { success: false, error: new Error('Preload not initialized') };
    }, [startPreload]);

    /**
     * Check if preloaded data is already available (synchronous check)
     */
    const isPreloadComplete = useCallback((): boolean => {
        return preloadState.data !== null;
    }, []);

    /**
     * Reset the preload state. Useful during logout.
     * Note: Stowage base data is global (same for all vessels) so this
     * typically doesn't need to be called when switching vessels.
     */
    const resetPreload = useCallback(() => {
        preloadState.promise = null;
        preloadState.data = null;
        preloadState.hasStarted = false;
    }, []);

    return {
        startPreload,
        getPreloadedData,
        isPreloadComplete,
        resetPreload,
    };
};

export default usePreloadBaseStowageData;
